import { type Component, type Editor } from 'grapesjs';

const BORDER_SIDES = ['top', 'right', 'bottom', 'left'] as const;
const BORDER_PROPS = ['width', 'style', 'color'] as const;

const RADIUS_CORNERS = [
  'border-top-left-radius',
  'border-top-right-radius',
  'border-bottom-right-radius',
  'border-bottom-left-radius',
] as const;

const allEqualFour = (values: unknown[]): boolean =>
  values.length === 4 && values.every((v) => v === values[0]);

const collapseBorderShorthand = (
  style: Record<string, unknown>
): Record<string, unknown> => {
  const next = { ...style };

  for (const prop of BORDER_PROPS) {
    const sideKeys = BORDER_SIDES.map((side) => `border-${side}-${prop}`);
    const values = sideKeys.map((k) => next[k]);

    if (!values.every((v) => v !== undefined && v !== null)) continue;
    if (!allEqualFour(values)) continue;

    next[`border-${prop}`] = values[0];
    sideKeys.forEach((k) => delete next[k]);
  }

  const bw = next['border-width'];
  const bs = next['border-style'];
  const bc = next['border-color'];
  if (bw !== undefined && bs !== undefined && bc !== undefined) {
    next['border'] = `${String(bw)} ${String(bs)} ${String(bc)}`;
    delete next['border-width'];
    delete next['border-style'];
    delete next['border-color'];
  }

  return next;
};

const collapseRadiusShorthand = (
  style: Record<string, unknown>
): Record<string, unknown> => {
  const next = { ...style };

  const values = RADIUS_CORNERS.map((k) => next[k]);
  if (!values.every((v) => v !== undefined && v !== null)) return next;

  next['border-radius'] = values.join(' ');
  RADIUS_CORNERS.forEach((k) => delete next[k]);

  return next;
};

export const normalizeBorderRuleById = (
  editor: Editor,
  id: string | undefined
): void => {
  if (!id) return;
  const trimmed = id.trim();
  if (!trimmed) return;

  const rule = editor.CssComposer.getRule(`#${trimmed}`);
  const style = rule?.getStyle?.() as Record<string, unknown> | undefined;
  if (!rule || !style) return;

  rule.setStyle(collapseRadiusShorthand(collapseBorderShorthand(style)));
};

const COMPONENT_SELECTED_KEY = '_gbsStyleManagerBorderNormalize';

type EditorWithNormalizeHandler = Editor & {
  [COMPONENT_SELECTED_KEY]?: (component: Component) => void;
};

/**
 * GrapesJS StyleManager border-clear fix.
 *
 * ## Problem
 * When a template is loaded from HTML the browser's CSS parser (CSSOM) expands
 * any border shorthand — `border: 7px solid black` — into up to 12 individual
 * properties. GrapesJS stores those expanded properties as-is in each
 * component's #id CSS rule (avoidInlineStyle mode).
 *
 * GrapesJS's built-in `border` PropertyComposite manages only three sub-keys:
 * `border-width`, `border-style`, `border-color`. Clicking "clear all" removes
 * those three keys but leaves the 12 expanded ones untouched — so the border
 * visually remains.
 *
 * ## Fix
 * On `component:selected`, collapses any expanded per-side border properties
 * back to shorthand (`border-top-width` × 4 → `border-width`, etc.) in the
 * component's #id CSS rule. After normalization, the Style Manager composite
 * only sees the three shorthand sub-keys, so GrapesJS's native "clear all"
 * removes them completely without any interception needed.
 */
export const StyleManagerBorderClearPlugin = (editor: Editor): void => {
  const editorExt = editor as EditorWithNormalizeHandler;
  const previous = editorExt[COMPONENT_SELECTED_KEY];
  if (previous) {
    editor.off('component:selected', previous);
  }

  const onComponentSelected = (component: Component): void => {
    normalizeBorderRuleById(editor, component.getId());
  };

  editorExt[COMPONENT_SELECTED_KEY] = onComponentSelected;
  editor.on('component:selected', onComponentSelected);
};
